import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack, Alert,
  LinearProgress, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, InputLabel, FormControl,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  MenuBook as ReflectionsIcon,
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  LightbulbOutlined as InsightIcon,
  Edit as EditIcon,
  AutoAwesome as GuidedIcon,
  Whatshot as StreakIcon,
  Summarize as DigestIcon,
  TrendingUp as PatternIcon,
  Flag as DriverIcon,
} from '@mui/icons-material';
import { format, subDays, parseISO } from 'date-fns';
import { reflectionsApi, driversApi } from '../api/services';
import DictationTextField from '../components/DictationTextField';
import GuidedReflectionDialog from '../components/GuidedReflectionDialog';
import CoachingInsightsCard from '../components/CoachingInsightsCard';
import {
  detectPatterns,
  describePattern,
  findDriverMentions,
  type DriverLike,
  type PatternTheme,
} from '../utils/reflectionPatterns';

interface Reflection {
  id: string;
  date: string;
  trigger: string;
  content: string;
  insights?: string;
  adjustmentsMade?: string;
  createdAt: string;
}

export default function ReflectionsPage() {
  const theme = useTheme();

  const TRIGGER_LABELS: Record<string, { label: string; color: string }> = useMemo(() => ({
    CloseDay: { label: 'Close Day', color: theme.palette.primary.main },
    MiddayReset: { label: 'Midday Reset', color: theme.palette.warning.main },
    WeeklyReset: { label: 'Weekly Reset', color: theme.palette.secondary.main },
    Manual: { label: 'Manual', color: theme.palette.success.main },
    PlanTomorrow: { label: 'Plan Tomorrow', color: theme.palette.secondary.light },
  }), [theme]);

  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [todayReflections, setTodayReflections] = useState<Reflection[]>([]);
  const [pastReflections, setPastReflections] = useState<Reflection[]>([]);
  const [allRecentReflections, setAllRecentReflections] = useState<Reflection[]>([]); // 30 days for streak + patterns
  const [activeDrivers, setActiveDrivers] = useState<DriverLike[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add Reflection dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newReflection, setNewReflection] = useState({
    trigger: 'Manual',
    content: '',
    insights: '',
    adjustmentsMade: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [guidedOpen, setGuidedOpen] = useState(false);

  const loadReflections = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Load selected date reflections
      let todayData: Reflection[] = [];
      try {
        const result = await reflectionsApi.getByDate(selectedDate);
        todayData = Array.isArray(result) ? result : result ? [result] : [];
      } catch (err: any) {
        if (err.response?.status !== 404) throw err;
      }
      setTodayReflections(todayData);

      // Load past 30 days (7 for display, 30 for streak tracking)
      const pastResults: Reflection[] = [];
      const allResults: Reflection[] = [];
      for (let i = 1; i <= 30; i++) {
        const pastDate = format(subDays(parseISO(selectedDate), i), 'yyyy-MM-dd');
        try {
          const result = await reflectionsApi.getByDate(pastDate);
          const items = Array.isArray(result) ? result : result ? [result] : [];
          allResults.push(...items);
          if (i <= 7) pastResults.push(...items);
        } catch {
          // 404 is expected for days without reflections
        }
      }
      setPastReflections(pastResults);
      setAllRecentReflections([...todayData, ...allResults]);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load reflections');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { loadReflections(); }, [loadReflections]);

  // Driver fetch is done once — we only need titles for the linkage scan,
  // and the list is small enough that skipping a re-fetch on every date change
  // is a worthwhile saving.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const drivers = await driversApi.getActive();
        if (cancelled) return;
        setActiveDrivers((drivers || []).map((d: any) => ({ id: d.id, title: d.title })));
      } catch {
        // Driver fetch is non-critical for this page — surface silently.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleCreateReflection = async () => {
    if (!newReflection.content.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await reflectionsApi.create({
        date: selectedDate,
        trigger: newReflection.trigger,
        content: newReflection.content,
        insights: newReflection.insights || undefined,
        adjustmentsMade: newReflection.adjustmentsMade || undefined,
      });
      setDialogOpen(false);
      setNewReflection({ trigger: 'Manual', content: '', insights: '', adjustmentsMade: '' });
      setSaveSuccess('Reflection saved successfully.');
      loadReflections();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save reflection');
    } finally {
      setSaving(false);
    }
  };

  const getTriggerInfo = (trigger: string) => TRIGGER_LABELS[trigger] || { label: trigger, color: theme.palette.text.secondary };

  // Streak tracking: count consecutive days with reflections (current + longest)
  const { currentStreak, longestStreak } = useMemo(() => {
    if (allRecentReflections.length === 0) return { currentStreak: 0, longestStreak: 0 };
    const dates = Array.from(new Set(allRecentReflections.map(r => r.date))).sort().reverse();

    // Current streak: consecutive days ending at today
    let current = 0;
    const today = new Date().toISOString().split('T')[0];
    let checkDate = today;
    for (const d of dates) {
      if (d === checkDate) {
        current++;
        const prev = new Date(checkDate);
        prev.setDate(prev.getDate() - 1);
        checkDate = prev.toISOString().split('T')[0];
      } else if (d < checkDate) {
        break;
      }
    }

    // Longest streak: find the longest run of consecutive days in the data
    const sortedAsc = [...dates].sort();
    let longest = 0;
    let run = 1;
    for (let i = 1; i < sortedAsc.length; i++) {
      const prev = new Date(sortedAsc[i - 1]);
      prev.setDate(prev.getDate() + 1);
      if (prev.toISOString().split('T')[0] === sortedAsc[i]) {
        run++;
      } else {
        longest = Math.max(longest, run);
        run = 1;
      }
    }
    longest = Math.max(longest, run);

    return { currentStreak: current, longestStreak: longest };
  }, [allRecentReflections]);

  // Weekly reflection digest — count only.
  const weekDigest = useMemo(() => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const weekReflections = allRecentReflections.filter(r => {
      const d = new Date(r.date);
      return d >= weekAgo;
    });
    if (weekReflections.length === 0) return null;
    return { count: weekReflections.length };
  }, [allRecentReflections]);

  // Pattern surfacing: scan the trailing 30 days for recurring themes. Only
  // themes that appear on two or more distinct days surface — a single bad
  // day shouldn't become "a pattern the exec is battling".
  const patterns: PatternTheme[] = useMemo(
    () => detectPatterns(allRecentReflections),
    [allRecentReflections],
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReflectionsIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Box>
            <Typography variant="h4">Reflections</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Daily journal and insights
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            type="date"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            startIcon={<GuidedIcon />}
            onClick={() => setGuidedOpen(true)}
          >
            Guided Reflection
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Quick Add
          </Button>
        </Stack>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {saveSuccess && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveSuccess(null)}>{saveSuccess}</Alert>}

      {/* Coaching insights land here so the close-day reflection flow
          surfaces the AI's nudges (stalled driver, red project, carry-
          forward tail) at the exact moment the exec is looking back on the
          day. The card self-hides when there is nothing to say. */}
      <CoachingInsightsCard title="Coaching for today" />

      {/* Streak & Weekly Digest */}
      {!loading && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          {/* Streak card */}
          <Card sx={{ flex: 1, bgcolor: 'background.paper', borderLeft: 3, borderColor: currentStreak > 0 ? 'secondary.main' : 'text.secondary' }}>
            <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <StreakIcon sx={{ fontSize: 20, color: currentStreak > 0 ? 'secondary.main' : 'text.secondary' }} />
                <Typography variant="body2" fontWeight={600} sx={{ color: currentStreak > 0 ? 'secondary.main' : 'text.secondary' }}>
                  {currentStreak > 0 ? `${currentStreak} day streak` : 'Start your streak today'}
                </Typography>
                {longestStreak > 0 && (
                  <Chip
                    label={`Best: ${longestStreak}d`}
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.secondary.main, 0.12),
                      color: 'secondary.main',
                      fontWeight: 600,
                      height: 22,
                    }}
                  />
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Weekly digest card */}
          {weekDigest && (
            <Card sx={{ flex: 1, bgcolor: 'background.paper', borderLeft: 3, borderColor: 'primary.main' }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <DigestIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    <strong>This week:</strong> {weekDigest.count} reflection{weekDigest.count !== 1 ? 's' : ''}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      )}

      {/* Pattern surfacing strip. Only rendered when we actually found a
          recurring theme across 2+ distinct days in the last 30. */}
      {!loading && patterns.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: 'background.paper', borderLeft: 3, borderColor: 'warning.main' }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <PatternIcon sx={{ fontSize: 20, color: 'warning.main' }} />
              <Typography variant="body2" fontWeight={600} sx={{ color: 'warning.main' }}>
                Recurring themes
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Last 30 days
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              {patterns.slice(0, 5).map((p) => (
                <Typography key={p.key} variant="body2" sx={{ color: 'text.primary' }}>
                  {describePattern(p)}
                </Typography>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Today's Reflections */}
      <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}>
        {selectedDate === format(new Date(), 'yyyy-MM-dd') ? "Today's Reflections" : `Reflections for ${format(parseISO(selectedDate), 'dd MMM yyyy')}`}
      </Typography>

      {!loading && todayReflections.length === 0 && (
        <Card sx={{ bgcolor: 'background.paper', mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <ReflectionsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary">No reflections yet</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Reflections are captured when you close your day, or you can add one manually.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Add Reflection
            </Button>
          </CardContent>
        </Card>
      )}

      <Stack spacing={2} sx={{ mb: 4 }}>
        {todayReflections.map((reflection) => (
          <ReflectionCard key={reflection.id} reflection={reflection} getTriggerInfo={getTriggerInfo} drivers={activeDrivers} />
        ))}
      </Stack>

      {/* Past Reflections */}
      {pastReflections.length > 0 && (
        <>
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 2, mt: 3 }}>
            Past 7 Days
          </Typography>
          <Stack spacing={2}>
            {pastReflections.map((reflection) => (
              <ReflectionCard key={reflection.id} reflection={reflection} getTriggerInfo={getTriggerInfo} drivers={activeDrivers} />
            ))}
          </Stack>
        </>
      )}

      {/* Add Reflection Dialog — three-prompt shape */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add reflection
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Three prompts. Keep them short.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Trigger</InputLabel>
              <Select
                value={newReflection.trigger}
                label="Trigger"
                onChange={e => setNewReflection(prev => ({ ...prev, trigger: e.target.value }))}
              >
                <MenuItem value="Manual">Manual</MenuItem>
                <MenuItem value="CloseDay">Close Day</MenuItem>
                <MenuItem value="MiddayReset">Midday Reset</MenuItem>
                <MenuItem value="WeeklyReset">Weekly Reset</MenuItem>
                <MenuItem value="PlanTomorrow">Plan Tomorrow</MenuItem>
              </Select>
            </FormControl>
            <DictationTextField
              label="What worked"
              multiline
              rows={3}
              fullWidth
              value={newReflection.content}
              onChange={e => setNewReflection(prev => ({ ...prev, content: (e.target as HTMLTextAreaElement).value }))}
              onTranscriptChange={text => setNewReflection(prev => ({ ...prev, content: text }))}
              placeholder="What went well today? Where did you make progress?"
            />
            <DictationTextField
              label="What didn't"
              multiline
              rows={3}
              fullWidth
              value={newReflection.insights}
              onChange={e => setNewReflection(prev => ({ ...prev, insights: (e.target as HTMLTextAreaElement).value }))}
              onTranscriptChange={text => setNewReflection(prev => ({ ...prev, insights: text }))}
              placeholder="What slipped, got stuck, or drained you?"
            />
            <DictationTextField
              label="What changes tomorrow"
              multiline
              rows={2}
              fullWidth
              value={newReflection.adjustmentsMade}
              onChange={e => setNewReflection(prev => ({ ...prev, adjustmentsMade: (e.target as HTMLTextAreaElement).value }))}
              onTranscriptChange={text => setNewReflection(prev => ({ ...prev, adjustmentsMade: text }))}
              placeholder="One thing you will do differently."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateReflection}
            disabled={saving || !newReflection.content.trim()}
          >
            {saving ? 'Saving...' : 'Save Reflection'}
          </Button>
        </DialogActions>
      </Dialog>

      <GuidedReflectionDialog
        open={guidedOpen}
        onClose={() => setGuidedOpen(false)}
        onSaved={() => { setGuidedOpen(false); loadReflections(); setSaveSuccess('Guided reflection saved successfully.'); }}
        date={selectedDate}
      />
    </Box>
  );
}

function ReflectionCard({
  reflection,
  getTriggerInfo,
  drivers,
}: {
  reflection: Reflection;
  getTriggerInfo: (trigger: string) => { label: string; color: string };
  drivers: DriverLike[];
}) {
  const theme = useTheme();
  const triggerInfo = getTriggerInfo(reflection.trigger);
  let displayDate: string;
  try {
    displayDate = format(parseISO(reflection.date), 'EEEE, dd MMM yyyy');
  } catch {
    displayDate = reflection.date;
  }

  const driverMentions = useMemo(
    () => findDriverMentions(reflection, drivers),
    [reflection, drivers],
  );

  return (
    <Card sx={{ bgcolor: 'background.paper', borderLeft: 3, borderColor: triggerInfo.color }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {displayDate}
            </Typography>
            <Chip
              label={triggerInfo.label}
              size="small"
              sx={{
                bgcolor: alpha(triggerInfo.color, 0.12),
                color: triggerInfo.color,
                fontWeight: 600,
                height: 22,
              }}
            />
            {driverMentions.map((m) => (
              <Chip
                key={m.driverId}
                icon={<DriverIcon sx={{ fontSize: 14 }} />}
                label={m.driverTitle}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.secondary.main, 0.12),
                  color: 'secondary.main',
                  fontWeight: 500,
                  height: 22,
                }}
              />
            ))}
          </Stack>
          {reflection.createdAt && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {format(parseISO(reflection.createdAt), 'HH:mm')}
            </Typography>
          )}
        </Box>

        {reflection.content && (
          <Box sx={{ mb: reflection.insights || reflection.adjustmentsMade ? 1.5 : 0 }}>
            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 0.25 }}>
              What worked
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
              {reflection.content}
            </Typography>
          </Box>
        )}

        {reflection.insights && (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.06), border: `1px solid ${alpha(theme.palette.warning.main, 0.12)}` }}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
              <InsightIcon sx={{ fontSize: 16, color: 'warning.main' }} />
              <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                What didn&apos;t
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
              {reflection.insights}
            </Typography>
          </Box>
        )}

        {reflection.adjustmentsMade && (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.06), border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}` }}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
              <EditIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                What changes tomorrow
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
              {reflection.adjustmentsMade}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
